import { useEffect, useRef, useState, useCallback } from 'react';

// ── SNAKE GAME ────────────────────────────────────────────────────────────────

const COLS = 26, ROWS = 12;

function SnakeGame({ onExit }) {
  const [state, setState] = useState(() => ({
    snake: [[6, 8], [6, 7], [6, 6]],
    dir: [0, 1],
    food: [4, 16],
    score: 0,
    dead: false,
  }));
  const dirRef = useRef([0, 1]);
  const boxRef = useRef(null);

  useEffect(() => { boxRef.current?.focus(); }, []);

  useEffect(() => {
    if (state.dead) return;
    const id = setInterval(() => {
      setState(s => {
        if (s.dead) return s;
        const dir = dirRef.current;
        const head = [s.snake[0][0] + dir[0], s.snake[0][1] + dir[1]];
        const hitWall = head[0] < 0 || head[0] >= ROWS || head[1] < 0 || head[1] >= COLS;
        const hitSelf = s.snake.some(([r, c]) => r === head[0] && c === head[1]);
        if (hitWall || hitSelf) return { ...s, dead: true };
        const ate = head[0] === s.food[0] && head[1] === s.food[1];
        const snake = [head, ...s.snake.slice(0, ate ? s.snake.length : -1)];
        let food = s.food;
        if (ate) {
          const occupied = new Set(snake.map(([r, c]) => `${r},${c}`));
          const free = [];
          for (let r = 0; r < ROWS; r++)
            for (let c = 0; c < COLS; c++)
              if (!occupied.has(`${r},${c}`)) free.push([r, c]);
          food = free[Math.floor(Math.random() * free.length)] || s.food;
        }
        return { ...s, snake, food, score: ate ? s.score + 10 : s.score, dir };
      });
    }, 130);
    return () => clearInterval(id);
  }, [state.dead]);

  const onKey = e => {
    const d = dirRef.current;
    const map = {
      ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1],
      w: [-1, 0], s: [1, 0], a: [0, -1], d: [0, 1],
    };
    if (e.key === 'q' || e.key === 'Escape') { onExit(state.score); return; }
    if (state.dead && e.key === 'Enter') { onExit(state.score); return; }
    const nd = map[e.key];
    if (nd && !(nd[0] === -d[0] && nd[1] === -d[1])) {
      e.preventDefault();
      dirRef.current = nd;
    }
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) e.preventDefault();
  };

  const grid = [];
  for (let r = 0; r < ROWS; r++) {
    let row = '';
    for (let c = 0; c < COLS; c++) {
      const isHead = state.snake[0][0] === r && state.snake[0][1] === c;
      const isBody = state.snake.some(([sr, sc], i) => i > 0 && sr === r && sc === c);
      const isFood = state.food[0] === r && state.food[1] === c;
      row += isHead ? '█' : isBody ? '▓' : isFood ? '◆' : '·';
    }
    grid.push(row);
  }

  return (
    <div className="snake-box" tabIndex={0} ref={boxRef} onKeyDown={onKey}>
      <div className="snake-hud">
        SNAKE · score {state.score} · arrows/wasd to move · q to quit
        {state.dead && <span className="snake-dead"> — GAME OVER (enter to exit)</span>}
      </div>
      <pre className="snake-grid">{grid.join('\n')}</pre>
    </div>
  );
}

// ── TERMINAL ──────────────────────────────────────────────────────────────────

const BANNER = [
  '  ███████ ██████   ██████ ',
  '  ██      ██   ██ ██      ',
  '  ███████ ██████  ██      ',
  '       ██ ██      ██      ',
  '  ███████ ██       ██████   v2.0',
  '',
  'Welcome to SpreadCode OS. Type `help` to see available commands.',
];

const COMMANDS = {
  help: () => [
    'Available commands:',
    '',
    '  about        who we are',
    '  services     what we build',
    '  stack        technologies we ship with',
    '  projects     selected work',
    '  contact      how to reach us',
    '  snake        play snake 🐍',
    '  sudo hire-us initiate a project',
    '  whoami       identify yourself',
    '  clear        clear the terminal',
    '',
    'Pro tip: there may be undocumented commands...',
  ],
  about: () => [
    'SpreadCode — an AI-first software agency.',
    'We build AI agents, Web3 systems, SaaS products & cloud infrastructure.',
    '20,000+ hours engineered.',
    'Based in Islamabad, operating worldwide.',
  ],
  services: () => [
    '▸ AI Agents & Automation .... LangChain, OpenAI, AutoGen',
    '▸ Web3 & Blockchain ......... Solidity, Ethers.js, IPFS',
    '▸ DevOps & Cloud ............ AWS, K8s, Terraform',
    '▸ SaaS Development .......... Next.js, Node, Postgres',
    '▸ PropTech & RETS ........... MLS API, IDX, Mapbox',
    '▸ Self-Sovereign Identity ... DIDs, VCs, Hyperledger',
  ],
  stack: () => [
    'react next.js node python typescript postgresql',
    'redis docker kubernetes aws langchain solidity hyperledger',
  ],
  projects: () => [
    'NeuralDesk ... AI support platform, -68% ticket volume',
    'ChainVault ... multi-chain DeFi tracker, 12 networks',
    'PropEdge ..... real estate analytics, 2M+ listings',
    'IdentityOS ... W3C DID credential platform',
  ],
  contact: () => [
    'email:  connect@spreadcode.org',
    'avg response time: < 2 hours',
  ],
  whoami: () => ['guest — but you could be our next client. Try `sudo hire-us`.'],
  'sudo hire-us': () => [
    '[sudo] password for guest: ********',
    'Access granted. Initiating partnership protocol...',
    '████████████████████████ 100%',
    '',
    '✓ Opening channel → connect@spreadcode.org',
    'We usually reply within 2 hours. Talk soon.',
  ],
  ls: () => ['ai-agents/  web3/  saas/  devops/  proptech/  ssi/  README.md'],
  'cat readme.md': () => ['# SpreadCode', 'We build software that defines tomorrow.'],
  pwd: () => ['/home/guest/spreadcode'],
  date: () => [new Date().toString()],
  coffee: () => ['☕ brewing... done. Productivity +200%.'],
  matrix: () => ['Wake up, Neo... the agency has you. Follow the white rabbit to `contact`.'],
  vim: () => ['You are now stuck in vim. Just kidding — this is our terminal, we let you leave.'],
  rm: () => ['rm: permission denied. Nice try though. 😏'],
  'rm -rf /': () => ['rm: permission denied. Nice try though. 😏'],
};

export default function Terminal() {
  const [lines, setLines] = useState(() => BANNER.map(t => ({ type: 'out', text: t })));
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [gameOn, setGameOn] = useState(false);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [lines, gameOn]);

  const run = useCallback(cmdRaw => {
    const cmd = cmdRaw.trim().toLowerCase();
    const echo = { type: 'cmd', text: cmdRaw };
    if (!cmd) { setLines(l => [...l, echo]); return; }
    setHistory(h => [cmdRaw, ...h]);
    setHistIdx(-1);
    if (cmd === 'clear') { setLines([]); return; }
    if (cmd === 'snake') {
      setLines(l => [...l, echo, { type: 'out', text: 'Launching snake...' }]);
      setGameOn(true);
      return;
    }
    if (cmd === 'sudo hire-us' || cmd === 'sudo hire us') {
      setLines(l => [...l, echo, ...COMMANDS['sudo hire-us']().map(t => ({ type: 'out', text: t }))]);
      setTimeout(() => { window.location.href = 'mailto:connect@spreadcode.org?subject=Project Inquiry via Terminal'; }, 1400);
      return;
    }
    const fn = COMMANDS[cmd];
    const out = fn
      ? fn().map(t => ({ type: 'out', text: t }))
      : [{ type: 'err', text: `command not found: ${cmd} — type \`help\`` }];
    setLines(l => [...l, echo, ...out]);
  }, []);

  const onKey = e => {
    if (e.key === 'Enter') { run(input); setInput(''); }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const ni = Math.min(histIdx + 1, history.length - 1);
      if (history[ni]) { setHistIdx(ni); setInput(history[ni]); }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const ni = histIdx - 1;
      setHistIdx(ni);
      setInput(ni >= 0 ? history[ni] : '');
    }
  };

  return (
    <div className="terminal reveal" onClick={() => !gameOn && inputRef.current?.focus()}>
      <div className="term-bar">
        <span className="term-dot td-r" /><span className="term-dot td-y" /><span className="term-dot td-g" />
        <span className="term-title">guest@spreadcode — zsh</span>
      </div>
      <div className="term-body" ref={bodyRef}>
        {lines.map((l, i) => (
          <div key={i} className={`term-line term-${l.type}`}>
            {l.type === 'cmd' && <span className="term-prompt">guest@spreadcode ~ % </span>}
            {l.text}
          </div>
        ))}
        {gameOn ? (
          <SnakeGame onExit={score => {
            setGameOn(false);
            setLines(l => [...l, { type: 'out', text: `snake exited — final score: ${score}. Fast reflexes. Imagine what we ship.` }]);
            setTimeout(() => inputRef.current?.focus(), 50);
          }} />
        ) : (
          <div className="term-input-row">
            <span className="term-prompt">guest@spreadcode ~ % </span>
            <input
              ref={inputRef}
              className="term-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              spellCheck={false}
              autoComplete="off"
              aria-label="Terminal input"
            />
          </div>
        )}
      </div>
    </div>
  );
}
